import { FlagFilled } from '@griply/assets';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SlimArrowUp } from 'shared/assets/icons';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import { TimeLeft } from 'shared/components/ui/time-left';
import { TimeProgressChart } from 'shared/components/ui/time-progress-chart';
import { DateEntry } from 'shared/lib/recharts';
import { Goal } from 'shared/types/goal';
import { metricUnitFormatBase } from 'shared/types/metric-template';
import { NumberFormat } from 'shared/types/number-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { formatNumberShort } from 'shared/utils/format-number-short';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './goal-insights-card.style';

// The header-agnostic content of an insights card: current value + completion
// circle + timeframe difference, and the TimeProgressChart footer. Shared by
// GoalInsightsCard and ProjectsInsightsCard so the value/progress + line chart
// stay single-source; only the header differs between the two.
export type InsightsCardContentProps = {
  goal: Goal;
  entries: DateEntry[];
  completionPercentage?: number;
  unitFormat?: string;
  startValue?: number;
  currentValue?: number;
  target?: number;
  timeframeDifference?: number;
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
  numberFormat?: NumberFormat;
  isEmpty?: boolean;
  isLink?: boolean;
  isLoading?: boolean;
  showLeftAxis?: boolean;
  // The metric-less variant: its presence swaps the value label, hides the progress
  // circle, and centres this action over an empty chart.
  noMetricAction?: React.ReactNode;
};

export const InsightsCardContent: React.FC<InsightsCardContentProps> = ({
  goal,
  entries,
  completionPercentage,
  unitFormat,
  startValue,
  currentValue,
  target,
  timeframeDifference,
  startDate,
  endDate,
  weekStartsOn,
  numberFormat,
  isEmpty,
  isLink,
  isLoading,
  showLeftAxis,
  noMetricAction,
}) => {
  const { t } = useTranslation();
  const isNoMetric = !!noMetricAction;

  const lastEntryValue =
    currentValue ?? entries?.[entries.length - 1]?.value ?? startValue;
  const formattedTarget = isNumber(target)
    ? formatNumberShort(target!, numberFormat?.decimal)
    : undefined;
  const formattedLastEntryValue = formatNumberShort(
    lastEntryValue ?? 0,
    numberFormat?.decimal,
  );

  return (
    <>
      {isLoading ? (
        <Styled.LoadingBody />
      ) : (
        <Styled.Body $isFrozen={!!goal.frozenAt}>
          <Styled.CurrentProgress>
            {!!unitFormat ? (
              <>
                <Styled.CurrentProgressValue $isEmpty={!!isEmpty}>
                  {formattedTarget
                    ? formattedLastEntryValue
                    : unitFormat?.replace(
                        metricUnitFormatBase,
                        formattedLastEntryValue,
                      )}
                </Styled.CurrentProgressValue>
                {formattedTarget
                  ? ` / ${unitFormat?.replace(metricUnitFormatBase, formattedTarget)}`
                  : null}
              </>
            ) : (
              t(
                isNoMetric
                  ? 'general.labels.insights-card.no-metric.label'
                  : 'general.labels.insights-card.no-tasks.label',
              )
            )}
          </Styled.CurrentProgress>

          <Styled.ProgressContainer>
            <Styled.Progress>
              {!goal.completedAt && !!goal.deadline && (
                <TimeLeft
                  date={goal.deadline}
                  isShort
                  startIcon={FlagFilled}
                  isSmallIcon
                />
              )}
              {!isNoMetric && (
                <CircleProgress percentage={completionPercentage} isSmall />
              )}
            </Styled.Progress>

            {!!timeframeDifference && (
              <Styled.Difference
                $positive={
                  isNumber(startValue) &&
                  isNumber(target) &&
                  startValue! > target!
                    ? timeframeDifference < 0
                    : timeframeDifference > 0
                }
              >
                <Styled.DifferenceIconContainer $up={timeframeDifference > 0}>
                  <Icon icon={SlimArrowUp} />
                </Styled.DifferenceIconContainer>
                {Math.abs(Math.round(timeframeDifference * 10) / 10)
                  .toString()
                  .replace('.', numberFormat?.decimal ?? '.')}
                %
              </Styled.Difference>
            )}
          </Styled.ProgressContainer>
        </Styled.Body>
      )}

      {isLoading ? (
        <Styled.LoadingFooter />
      ) : (
        <Styled.Footer $isFrozen={!!goal.frozenAt} $isLink={!!isLink}>
          {isNoMetric && (
            <Styled.ChartOverlay>{noMetricAction}</Styled.ChartOverlay>
          )}
          <TimeProgressChart
            data={entries}
            startDate={goal.startDate}
            endDate={goal.deadline}
            chartStartDate={startDate}
            chartEndDate={endDate}
            startValue={startValue}
            endValue={target}
            weekStartsOn={weekStartsOn}
            isComplete={(completionPercentage ?? 0) >= 100}
            isEmpty={isEmpty}
            hideYAxis={!showLeftAxis}
            hasSmallHeight
            showTodayLine
            numberFormat={numberFormat}
          />
        </Styled.Footer>
      )}
    </>
  );
};
